export const isVariantInCart = (variantId: string, cartData: any[], productRenderedLocation: string): boolean => {
  if (productRenderedLocation === "external") {
    return cartData.some((item: any) => item.merchandise?.id === variantId);
  }

  // For Shopify cart
  const adminId = variantId.includes("gid://shopify/ProductVariant/") 
    ? variantId.replace("gid://shopify/ProductVariant/", "") 
    : variantId;

  return cartData.some((item: any) => {
    return item.key === variantId || 
           item.variant_id?.toString() === adminId || 
           item.variant_id === parseInt(adminId) || 
           item.id?.toString() === adminId;
  });
};

export const getCartQuantityForVariant = (variantId: string, cartData: any[], productRenderedLocation: string): number => {
  if (productRenderedLocation === "external") {
    const headlessItem = cartData.find((item: any) => item.merchandise?.id === variantId);
    return headlessItem?.quantity || 1;
  }

  // For Shopify cart
  const adminId = variantId.includes("gid://shopify/ProductVariant/") ? variantId.replace("gid://shopify/ProductVariant/", "") : variantId;

  const allVariantItems = cartData.filter((item: any) => {
    return item.key === variantId || item.variant_id?.toString() === adminId || item.variant_id === parseInt(adminId) || item.id?.toString() === adminId;
  });

  // Sum all quantities to handle membership discount duplicates
  const totalQuantity = allVariantItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  return totalQuantity || 1;
};

export const getCartLineSubtotalForVariant = (variantId: string, cartData: any[], productRenderedLocation: string): number | null => {
  if (productRenderedLocation === "external") {
    const lineItems = cartData.filter((item: any) => item.merchandise?.id === variantId);
    if (lineItems.length === 0) return null;

    return lineItems.reduce((sum: number, item: any) => {
      const price = Number(item.merchandise?.priceV2?.amount || item.merchandise?.price?.amount || item.merchandise?.price || 0);
      const quantity = item.quantity || 0;
      const discountTotal = (item.discountAllocations || []).reduce((acc: number, allocation: any) => {
        return acc + Number(allocation?.discountedAmount?.amount || 0);
      }, 0);
      const lineTotal = Math.max(0, price * quantity - discountTotal);
      return sum + lineTotal;
    }, 0);
  }

  const adminId = variantId.includes("gid://shopify/ProductVariant/") ? variantId.replace("gid://shopify/ProductVariant/", "") : variantId;
  const lineItems = cartData.filter((item: any) => {
    return item.key === variantId || item.variant_id?.toString() === adminId || item.variant_id === parseInt(adminId) || item.id?.toString() === adminId;
  });

  if (lineItems.length === 0) return null;

  const totalCents = lineItems.reduce((sum: number, item: any) => {
    // `final_line_price` only nets LINE-level discounts. Order/cart-level discounts
    // (race discount functions, postcode discounts, discount codes) reduce the cart's
    // `total_price` but are NOT folded into `final_line_price`, so summing it overstates
    // the Sub Total whenever such a discount applies (e.g. Sub Total $174 vs Total $114).
    // `total_discount` includes the order-level amount allocated to the line, so subtract
    // it from the pre-discount `original_line_price` to net ALL discounts and reconcile
    // with the real cart Total.
    const originalCents = Number(item.original_line_price ?? 0);
    if (originalCents > 0) {
      const discountCents = Number(item.total_discount || 0);
      return sum + Math.max(0, originalCents - discountCents);
    }

    // Fallback when `original_line_price` is unavailable: `final_line_price` at least
    // reflects line-level discounts.
    const lineTotalCents = item.final_line_price ?? item.line_price ?? (item.price || 0) * (item.quantity || 0);
    return sum + Number(lineTotalCents || 0);
  }, 0);

  return totalCents / 100;
};

export const getTargetCartItem = (variantId: string, cartData: any[], productRenderedLocation: string): { cartItemId: string; quantity: number } | null => {
  if (productRenderedLocation === "external") {
    const headlessItem = cartData.find((item: any) => item.merchandise?.id === variantId);
    return headlessItem ? { cartItemId: headlessItem.id, quantity: headlessItem.quantity || 1 } : null;
  }

  // For Shopify cart
  const adminId = variantId.includes("gid://shopify/ProductVariant/") ? variantId.replace("gid://shopify/ProductVariant/", "") : variantId;

  const allVariantItems = cartData.filter((item: any) => {
    return item.key === variantId || item.variant_id?.toString() === adminId || item.variant_id === parseInt(adminId) || item.id?.toString() === adminId;
  });

  if (allVariantItems.length === 0) return null;

  // For multiple items, prioritize non-discounted items for operations
  let targetItem = null;
  if (allVariantItems.length > 1) {
    targetItem = allVariantItems
      .filter((item) => (item.quantity || 0) > 0)
      .sort((a, b) => {
        const discountDiff = (a.total_discount || 0) - (b.total_discount || 0);
        if (discountDiff !== 0) return discountDiff;
        return (b.quantity || 0) - (a.quantity || 0);
      })[0];
  } else {
    targetItem = allVariantItems[0];
  }

  return targetItem ? { cartItemId: targetItem.key, quantity: targetItem.quantity || 1 } : null;
};


/** Product types whose cart lines participate in membership display pricing (mirrors SessionPills eligibility). */
const MEMBERSHIP_DISPLAY_PRICE_PRODUCT_TYPES = ["main", "mini", "junior", "intermediate", "battlekart", "battle kart", "virtual"];

const isMembershipDisplayPriceProductType = (productType: string): boolean => {
  const normalized = (productType || "").toLowerCase();
  return MEMBERSHIP_DISPLAY_PRICE_PRODUCT_TYPES.some((eligibleType) => normalized.includes(eligibleType));
};

/**
 * Sum of per-line deductions that brings the cart's real subtotal down to the membership
 * prices SessionPills displays for cart-backed lines (max(0, unitPrice - perUnitDiscount)
 * per unit, hence the clamp to the line total).
 *
 * Lines that already carry a real Shopify discount are skipped: their discounted cart
 * subtotal is already exactly what the pills display. Visitor (postcode) discounts must
 * NOT be passed in on their own — Shopify applies those to the cart itself per line
 * (clamped to each track's minimum price), so the only display-only discount to bridge
 * is membership pricing (plus the visitor amount membership display prices fold in).
 */
export const getMembershipDisplayPriceAdjustment = (cartData: any[], productRenderedLocation: string, perUnitDiscount: number): number => {
  if (perUnitDiscount <= 0 || !Array.isArray(cartData)) {
    return 0;
  }

  if (productRenderedLocation === "external") {
    return cartData.reduce((sum: number, item: any) => {
      const productType = item.merchandise?.product?.productType || "";
      if (!isMembershipDisplayPriceProductType(productType)) return sum;

      const quantity = Number(item.quantity || 0);
      const discountTotal = (item.discountAllocations || []).reduce((acc: number, allocation: any) => {
        return acc + Number(allocation?.discountedAmount?.amount || 0);
      }, 0);
      if (discountTotal > 0) return sum;

      const unitPrice = Number(item.merchandise?.priceV2?.amount || item.merchandise?.price?.amount || item.merchandise?.price || 0);
      const lineTotal = Math.max(0, unitPrice * quantity);
      return sum + Math.min(lineTotal, perUnitDiscount * quantity);
    }, 0);
  }

  return cartData.reduce((sum: number, item: any) => {
    const productType = item.product_type || "";
    if (!isMembershipDisplayPriceProductType(productType)) return sum;

    const quantity = Number(item.quantity || 0);
    const totalDiscount = Number(item.total_discount || 0);
    if (totalDiscount > 0) return sum;

    const lineTotalCents = item.final_line_price ?? item.line_price ?? (item.price || 0) * quantity;
    const lineTotal = Number(lineTotalCents || 0) / 100;
    return sum + Math.min(lineTotal, perUnitDiscount * quantity);
  }, 0);
};
